import React, { useEffect, useState } from "react";
import dayjs from "dayjs";
import Title from "../../extra/Title";
import { apiInstanceFetch } from "../../../util/api";

const INK = "#1A1614";
const MUTED = "#8A857F";
const HAIRLINE = "#E8E1D3";
const GOLD = "#D4A129";
const CREAM = "#FFFDF7";
const RED = "#DC2626";
const REDBG = "#FDECEC";
const GREEN = "#15803D";
const GREENBG = "#E8F5EC";

const STEP_LABEL = { login: "Joining room", publish: "Starting video", other: "Other" };
const OUTCOME_LABEL = { ok: "Started", failed: "Failed", timeout: "Timed out", cancelled: "Cancelled" };

// Zego error codes (from ZEGOCLOUD's error-code reference). Only the codes a
// live start can hit are named; the raw code is always shown too.
const ZEGO_CODE = {
  1002001: "Already logged in to a room (previous live not logged out)",
  1002005: "User ID empty",
  1002011: "Room ID empty",
  1002012: "Room ID has illegal characters",
  1002030: "Login failed, network unavailable",
  1002031: "Login timed out, high network latency",
  1002033: "Authentication failed (appSign/token)",
  1002034: "Room user limit exceeded",
  1002035: "Concurrent room limit exceeded",
  1002050: "Kicked out: same user logged in on another device",
  1002051: "Room connection interrupted, retrying",
  1002052: "Room connection disconnected",
  1002053: "Login retries exceeded",
  1002064: "Room ID already in use by a login call",
  1002066: "User is on Zego's blacklist",
  1002086: "Already logged in on another device",
  1002099: "Zego internal error",
  1003001: "No camera/mic data to publish",
  1003020: "Publishing interrupted, retrying",
  1003021: "Publishing retries exceeded",
  1003023: "Channel already publishing",
  1003025: "Publishing blocked by Zego backend config",
  1003028: "Stream ID already exists in the room",
  1003072: "Publish authentication failed",
  1003099: "Zego internal error",
};

const pct = (failed, n) => (n ? `${Math.round((failed / n) * 100)}%` : "—");

// Live-start attempts reported by the host's phone (2026-10-11). Before this
// page the server only knew that a live "never started" (reaper); the Zego
// result code behind each failure lived on the phone and was lost.
const LiveStarts = () => {
  const [data, setData] = useState({ rows: [], total: 0, byOutcome: [], byCode: [], byPlatform: [], byVersion: [], byNetwork: [] });
  const [days, setDays] = useState(7);
  const [outcome, setOutcome] = useState("");
  const [platform, setPlatform] = useState("");
  const [loading, setLoading] = useState(false);

  const load = () => {
    setLoading(true);
    apiInstanceFetch
      .get(`liveStart/admin/list?days=${days}&limit=300${outcome ? `&outcome=${outcome}` : ""}${platform ? `&platform=${platform}` : ""}`)
      .then((r) =>
        setData({
          rows: r.rows || [],
          total: r.total || 0,
          byOutcome: r.byOutcome || [],
          byCode: r.byCode || [],
          byPlatform: r.byPlatform || [],
          byVersion: r.byVersion || [],
          byNetwork: r.byNetwork || [],
        })
      )
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days, outcome, platform]);

  const userName = (u) => {
    if (!u) return "—";
    return ((u.firstName || "") + " " + (u.lastName || "")).trim() || u.email || "—";
  };

  const attempts = data.byOutcome.reduce((a, s) => a + s.n, 0);
  const okCount = (data.byOutcome.find((s) => s._id === "ok") || { n: 0 }).n;
  const failedCount = attempts - okCount;

  const chip = (label, n, active, onClick) => (
    <button
      key={label}
      onClick={onClick}
      className="btn btn-sm"
      style={{
        border: `1px solid ${active ? GOLD : HAIRLINE}`,
        background: active ? "#FFF6D6" : "#fff",
        color: INK,
        fontWeight: 600,
        marginRight: 6,
        marginBottom: 6,
      }}
    >
      {label} <span style={{ color: MUTED }}>({n})</span>
    </button>
  );

  const badge = (o) => (
    <span
      style={{
        background: o === "ok" ? GREENBG : REDBG,
        color: o === "ok" ? GREEN : RED,
        padding: "2px 8px",
        borderRadius: 10,
        fontWeight: 700,
        fontSize: 12,
        whiteSpace: "nowrap",
      }}
    >
      {OUTCOME_LABEL[o] || o}
    </span>
  );

  const codeLabel = (c) => (c == null ? "" : ZEGO_CODE[c] ? `${c} · ${ZEGO_CODE[c]}` : String(c));

  return (
    <div className="mainCategory">
      <Title name="Live Starts" />

      <div className="d-flex align-items-center flex-wrap" style={{ gap: 10, margin: "16px 0" }}>
        <i className="fa-solid fa-tower-broadcast" style={{ color: GOLD }} />
        <span style={{ fontWeight: 700, color: INK, fontSize: 15 }}>
          {attempts} Go Live attempt{attempts === 1 ? "" : "s"}, {failedCount} failed ({pct(failedCount, attempts)}) in the last
        </span>
        <select
          value={days}
          onChange={(e) => setDays(Number(e.target.value))}
          className="form-select form-select-sm"
          style={{ width: 120, border: `1px solid ${HAIRLINE}` }}
        >
          <option value={1}>24 hours</option>
          <option value={3}>3 days</option>
          <option value={7}>7 days</option>
          <option value={30}>30 days</option>
          <option value={90}>90 days</option>
        </select>
        {loading && <span style={{ color: MUTED, fontSize: 12 }}>loading…</span>}
      </div>

      <div style={{ marginBottom: 6 }}>
        <span style={{ color: MUTED, fontSize: 12, marginRight: 8 }}>Outcome:</span>
        {chip("All", attempts, outcome === "", () => setOutcome(""))}
        {data.byOutcome.map((s) => chip(OUTCOME_LABEL[s._id] || s._id, s.n, outcome === s._id, () => setOutcome(outcome === s._id ? "" : s._id)))}
      </div>
      <div style={{ marginBottom: 6 }}>
        <span style={{ color: MUTED, fontSize: 12, marginRight: 8 }}>Platform:</span>
        {chip("All", attempts, platform === "", () => setPlatform(""))}
        {data.byPlatform.map((s) => chip(`${s._id || "unknown"} · ${pct(s.failed, s.n)} fail`, s.n, platform === s._id, () => setPlatform(platform === s._id ? "" : s._id)))}
        {data.byNetwork.length > 0 && (
          <span style={{ color: MUTED, fontSize: 12, marginLeft: 12 }}>
            Network: {data.byNetwork.map((v) => `${v._id || "?"} ${pct(v.failed, v.n)} fail (${v.n})`).join(" · ")}
          </span>
        )}
      </div>
      {data.byVersion.length > 0 && (
        <div style={{ color: MUTED, fontSize: 12, marginBottom: 10 }}>
          Versions: {data.byVersion.map((v) => `${v._id.platform || "?"} ${v._id.appVersion || "?"} ${pct(v.failed, v.n)} fail (${v.n})`).join(" · ")}
        </div>
      )}

      {data.byCode.length > 0 && (
        <div style={{ background: "#fff", border: `1px solid ${HAIRLINE}`, borderRadius: 14, padding: "10px 14px", marginBottom: 14 }}>
          <div style={{ fontWeight: 700, color: INK, marginBottom: 6 }}>Why starts failed</div>
          <table className="table table-sm mb-0" style={{ fontSize: 13, maxWidth: 640 }}>
            <thead>
              <tr style={{ color: MUTED }}>
                <th>Step</th>
                <th>Outcome</th>
                <th>Zego code</th>
                <th style={{ textAlign: "right" }}>Count</th>
              </tr>
            </thead>
            <tbody>
              {data.byCode.map((c, i) => (
                <tr key={i}>
                  <td>{STEP_LABEL[c._id.step] || c._id.step}</td>
                  <td>{OUTCOME_LABEL[c._id.outcome] || c._id.outcome}</td>
                  <td>{codeLabel(c._id.zegoCode) || "—"}</td>
                  <td style={{ textAlign: "right", fontWeight: 700 }}>{c.n}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div style={{ background: "#fff", border: `1px solid ${HAIRLINE}`, borderRadius: 14, overflow: "auto" }}>
        <table className="table mb-0" style={{ fontSize: 13 }}>
          <thead style={{ background: CREAM }}>
            <tr style={{ color: MUTED }}>
              <th>When</th>
              <th>Host</th>
              <th>Phone</th>
              <th>App</th>
              <th>Network</th>
              <th>Step</th>
              <th>Outcome</th>
              <th>Zego code</th>
              <th>Try</th>
              <th>Took</th>
              <th>Details</th>
            </tr>
          </thead>
          <tbody>
            {data.rows.length === 0 ? (
              <tr>
                <td colSpan={11} style={{ textAlign: "center", padding: 32, color: MUTED }}>
                  No live starts reported for this period (needs app build with live-start reports)
                </td>
              </tr>
            ) : (
              data.rows.map((r) => (
                <tr key={r._id}>
                  <td style={{ whiteSpace: "nowrap" }}>{dayjs(r.createdAt).format("DD MMM HH:mm")}</td>
                  <td>
                    <div>{userName(r.userId)}</div>
                    <div style={{ color: MUTED, fontSize: 12 }}>{r.hostType || ""}</div>
                  </td>
                  <td>
                    <div style={{ fontWeight: 600 }}>{r.deviceModel || "—"}</div>
                    <div style={{ color: MUTED, fontSize: 12 }}>{[r.platform, r.osVersion].filter(Boolean).join(" ")}</div>
                  </td>
                  <td>{r.appVersion || "—"}</td>
                  <td>{r.network || "—"}</td>
                  <td>{STEP_LABEL[r.step] || r.step}</td>
                  <td>{badge(r.outcome)}</td>
                  <td style={{ whiteSpace: "nowrap" }}>{codeLabel(r.zegoCode) || "—"}</td>
                  <td>{r.attempt || 1}</td>
                  <td style={{ whiteSpace: "nowrap" }}>{r.elapsedMs != null ? `${(r.elapsedMs / 1000).toFixed(1)}s` : "—"}</td>
                  <td style={{ maxWidth: 360, wordBreak: "break-word", color: INK }}>{r.error || "—"}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default LiveStarts;
